import React from "react";
import PropTypes from "prop-types";
import { expertUrl } from "views_shared/app/javascript/utils/urls";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import { initials } from "../../source_directory/utils";
import { SourceBadgeList } from "../../source_directory/SourceBadges";
import HighlightAttribute, { attributeMatched } from "../../source_directory/HighlightAttribute";
import ExpertJobLine from "./ExpertJobLine";
import isOwnExpert from "./viewerGating";
import expertListActionColumnWidth from "./listActionColumn";

function ExpertCardList({ hit }) {
  const job = hit.jobs?.[0];
  const headlineMatched = attributeMatched(hit, "headline");
  const canMessage = !!globalThis.Qwoted?.CURRENT_USER_ID && !isOwnExpert(hit);

  return (
    <div
      className="sd-list-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
      }}
    >
      {/* Name / photo / title */}
      <div
        className="sd-list-row__col"
        style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: "0 0 220px", minWidth: 0 }}
      >
        {hit.profile_image_url ? (
          <img
            src={hit.profile_image_url}
            alt={hit.name}
            style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 600,
              color: "#6b7280",
            }}
          >
            {initials(hit.name)}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            <a href={expertUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "inherit" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {(headlineMatched || job) && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              <ExpertJobLine hit={hit} job={job} headlineMatched={headlineMatched} />
            </div>
          )}
          <SourceBadgeList
            size="sm"
            marginTop={2}
            types={[hit.frequent_source && "frequent_source", hit.quick_to_respond && "fast_to_respond"]}
          />
        </div>
      </div>

      {/* Location */}
      <div
        className="sd-list-row__col"
        style={{ flex: "0 0 130px", fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}
      >
        {hit.location_string && (
          <>
            <LocationMarkerIcon size={12} />
            {hit.location_string}
          </>
        )}
      </div>

      {/* Bio */}
      <div className="sd-list-row__col" style={{ flex: 1, fontSize: 13, color: "#374151", minWidth: 0 }}>
        {hit.description && (
          <HighlightAttribute
            hit={hit}
            attribute="description"
            snippet
            fallback={hit.description.length > 120 ? `${hit.description.slice(0, 120)}…` : hit.description}
          />
        )}
      </div>

      {/* Actions */}
      <div
        className="sd-list-row__col"
        style={{
          flex: `0 0 ${expertListActionColumnWidth(canMessage)}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {canMessage && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <MessageButton
              promoEntityId={hit.id}
              promoEntityType="Id::PromoEntity::Expert"
              buttonLabel="Start a Conversation"
              extraButtonClasses="mb-0 w-100"
              openInboxThread
            />
          </div>
        )}
        <SavedGroupsBookmark
          sourceableType="Id::PromoEntity::Expert"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>
    </div>
  );
}

ExpertCardList.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    slug: PropTypes.string,
    profile_image_url: PropTypes.string,
    jobs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        company: PropTypes.string,
        company_slug: PropTypes.string,
      }),
    ),
    frequent_source: PropTypes.bool,
    quick_to_respond: PropTypes.bool,
    location_string: PropTypes.string,
    headline: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

export default ExpertCardList;
